import { isEmpty } from 'lodash';
import { Dispatch } from 'redux';
import { MaritalStatuses } from '../../../../global/constants/lists';
import { apiErrorClear } from '../../../../global/ducks/base-api';
import { getSpouses, patchDeath, patchDivorce, patchLegallySeparation, patchPersonInfo, patchSpouse, postSpouse, putSpouse, setPersonWithHandler } from '../../../../global/ducks/person-prc-api';
import { requiredDocUploadFieldChanged } from '../../../../global/helpers/doc-upload-helpers';
import { appendDocument, formatDate, parseChanged, parsePhone } from '../../../../global/helpers/form-data-helper';
import { ApiStatuses, YesNoIndicators } from '../../../../global/models/api-model';
import { DemographicAppKeys } from '../../../../global/models/demographics-model';
import { Person } from '../../../../global/models/person-model';
import { DocumentUploadState } from '../personal-info/document-upload-models';
import { processRequestWithDocumentUpload } from '../personal-info/document-upload-request';
import { setDocumentUploadStatus, toggleDocumentUpload } from '../personal-info/document-upload-slice';
import { dispatchSetCurrentPerson, dispatchSetCurrentPersonFailed, setPatchPersonStatus } from '../personal-info/personal-info-slice';
import { Spouse } from '../relationships/relationships-model';
import { dispatchSetSpouses, dispatchSetSpousesFailed, setDivorcedStatus, setLegalSeparationStatus, setPatchSpouseStatus, setPostSpouseStatus, setSpouseUnloaded, setWidowedStatus, updateSpouse, updateSpousePerson } from '../relationships/relationships-slice';
import { MaritalStatusFormValues } from './marital-form-models';
import { PersonalInfoPersonTypes } from '../personal-info/personal-info-models';
import { completePersonType } from '../../helpers/personal-info-helpers';
import { PointOfContactFormModel } from '../mycpg-midyear/models/mycpg-mid-year-models';

function clearErrorsAndStatus(dispatch: Dispatch<any>, app: string) {
	dispatch(setPostSpouseStatus(ApiStatuses.None, app));
	dispatch(setPatchSpouseStatus(ApiStatuses.None, app));
	dispatch(setPatchPersonStatus(ApiStatuses.None, app));
	dispatch(apiErrorClear());
}

export const handleSubmitPost = (
	docUploadState: DocumentUploadState,
	app: DemographicAppKeys,
	dispatch: Dispatch<any>,
	personGuid: string,
	initialValues: MaritalStatusFormValues,
	values: MaritalStatusFormValues,
	onSubmitSucess?: () => void) => {
	clearErrorsAndStatus(dispatch, app);

	const withPhoneUpdate = parsePhone(values) as MaritalStatusFormValues;
	const toUpdate = parseChanged(initialValues, withPhoneUpdate, true);

	const body = {
		...toUpdate,
		date_of_birth: formatDate(values.date_of_birth),
		start_date: formatDate(values.marital_status_effective_date),
		isMatchPerson: false,
	};

	delete body.marital_status;
	delete body.marital_status_effective_date;
	delete body.marriage_end_date;
	delete body.attestation1;
	delete body.attestation2;

	const postSpouseSuccess = () => {
		dispatch(setPersonWithHandler(personGuid, dispatchSetCurrentPerson(dispatch, app), () => dispatchSetCurrentPersonFailed(dispatch, app)(true)));
		dispatch(getSpouses(personGuid, false, dispatchSetSpouses(dispatch, app), () => dispatchSetSpousesFailed(dispatch, app)));
		dispatch(setPostSpouseStatus(ApiStatuses.Success, app));
		if (onSubmitSucess) onSubmitSucess();
	};

	const onDocUploadSuccess = (data) => {
		const withDocuments = appendDocument(body, data?.map(a => a?.data?.doc_id));
		dispatch(postSpouse(personGuid, withDocuments, () => {
			postSpouseSuccess();
		}));
	};

	if (docUploadState.stagedDocuments && docUploadState.stagedDocuments.length > 0) {
		processRequestWithDocumentUpload(docUploadState, personGuid, dispatch, onDocUploadSuccess);
	} else {

		dispatch(postSpouse(personGuid, body, () => {
			postSpouseSuccess();
		}));
	}

};

export const handleSubmitPut = (docUploadState: DocumentUploadState, app: DemographicAppKeys, dispatch: Dispatch<any>, guid: string, startDate: string, searchGuid: string, onSubmitSucess?: () => void) => {
	clearErrorsAndStatus(dispatch, app);

	const body = {
		start_date: formatDate(startDate),
		guid: searchGuid,
	};

	const onPutSpouseSuccess = () => {
		dispatch(setPersonWithHandler(guid, dispatchSetCurrentPerson(dispatch, app), () => dispatchSetCurrentPersonFailed(dispatch, app)(true)));
		dispatch(getSpouses(guid, false, dispatchSetSpouses(dispatch, app), () => dispatchSetSpousesFailed(dispatch, app)));
		dispatch(setPostSpouseStatus(ApiStatuses.Success, app));
		if (onSubmitSucess) onSubmitSucess();
	};

	const onDocUploadSuccess = (data) => {
		const withDocuments = appendDocument(body, data?.map(a => a?.data?.doc_id));
		dispatch(putSpouse(guid, withDocuments, onPutSpouseSuccess));
	};

	if (docUploadState.stagedDocuments && docUploadState.stagedDocuments.length > 0) {

		processRequestWithDocumentUpload(docUploadState, guid, dispatch, onDocUploadSuccess);

	} else {
		dispatch(putSpouse(guid, body, onPutSpouseSuccess));
	}


};



export const handleMarriedChange = (
	app: DemographicAppKeys,
	dispatch: Dispatch<any>,
	personGuid: string,
	savedValues: MaritalStatusFormValues,
	formValues: MaritalStatusFormValues,
	currentSpouse: Spouse,
	docUploadState: DocumentUploadState,
	memberGuid: string,
	onSubmitSucess?: () => void,
	skipDocUpload?: boolean,
) => {

	const withPhoneUpdate = parsePhone(formValues);
	const toUpdate: any = parseChanged(savedValues, withPhoneUpdate, true);

	completePersonType(toUpdate, memberGuid, PersonalInfoPersonTypes.Spouse);

	delete toUpdate.marriage_end_date;

	if (!docUploadState.documentUploaded && requiredDocUploadFieldChanged(toUpdate, skipDocUpload)) {
		dispatch(toggleDocumentUpload(true, app));
		return;
	}

	const { marital_status_effective_date, marital_status, ...spouseInfo } = toUpdate;

	const hasSpouseInfoChanges = !isEmpty({ ...spouseInfo });



	const patchSpouseSuccess = (spouse: Spouse, waitRedirect?: boolean) => {
		dispatch(updateSpouse(spouse, app));
		dispatch(setPatchSpouseStatus(ApiStatuses.Success, app));
		if (!waitRedirect && onSubmitSucess) onSubmitSucess();
	};

	const patchPersonSuccess = (person: Person) => {
		dispatch(updateSpousePerson(person, app));
		dispatch(setPatchPersonStatus(ApiStatuses.Success, app));
		dispatch(setDocumentUploadStatus(false, app));
		if (onSubmitSucess) onSubmitSucess();
	};

	const onDocUploadSuccess = (spouseChanges: any) => (data) => {
		const withDocuments = appendDocument(spouseChanges, data?.map(a => a?.data?.doc_id));
		dispatch(patchPersonInfo(currentSpouse.person_guid, withDocuments, skipDocUpload, (patchPersonResponse) => {
			patchPersonSuccess(patchPersonResponse.data);
		}));
	};

	const patchPersonWithDocUpload = () => {

		if (docUploadState.stagedDocuments && docUploadState.stagedDocuments.length > 0 && docUploadState.showDocumentUpload) {

			processRequestWithDocumentUpload(docUploadState, currentSpouse.person_guid, dispatch, onDocUploadSuccess(toUpdate));

		} else {
			dispatch(patchPersonInfo(currentSpouse.person_guid, { ...spouseInfo }, skipDocUpload, (patchPersonResponse) => {
				patchPersonSuccess(patchPersonResponse.data);
			}));
		}
	};

	if (marital_status_effective_date || hasSpouseInfoChanges) {
		clearErrorsAndStatus(dispatch, app);
	}

	// if need to patch marital status, do so, then patch spouse person info if need.
	// else, patch person info only
	if (marital_status_effective_date) {
		dispatch(patchSpouse(personGuid, currentSpouse.start_date, { start_date: marital_status_effective_date }, (patchSpouseResponse) => {
			patchSpouseSuccess(patchSpouseResponse.data, hasSpouseInfoChanges);
			if (hasSpouseInfoChanges) {
				patchPersonWithDocUpload();
			}
		}));
	} else if (hasSpouseInfoChanges) {
		patchPersonWithDocUpload();
	}
};


export const handleDivorceOrWidowed = (
	docUploadState: DocumentUploadState,
	app: DemographicAppKeys,
	dispatch: Dispatch<any>,
	personGuid: string,
	maritalStatus: MaritalStatuses,
	spouse: Spouse,
	savedValues: MaritalStatusFormValues,
	formValues: MaritalStatusFormValues,
	isWidowed: boolean,
	pointOfContactFormValues?: PointOfContactFormModel,
	onSubmitSucess?: () => void) => {

	let payload;
	if (isWidowed) {
		if (maritalStatus !== MaritalStatuses.Widowed || formatDate(savedValues.marriage_end_date) !== formatDate(formValues.marriage_end_date)) {

			payload = {
				preliminary_date_of_death: formatDate(formValues.marriage_end_date),
			};

			if (pointOfContactFormValues) {
				payload = {
					...payload,
					'dependent_type': pointOfContactFormValues.dependent_type,
				};

				if (pointOfContactFormValues.is_contact_person === YesNoIndicators.Yes) {
					payload = {
						...payload,
						'contact_phone': `${pointOfContactFormValues.your_contact_phone_country_code} ${pointOfContactFormValues.your_contact_phone_formated}`,
						'contact_email': pointOfContactFormValues.your_contact_email,
						'contact_name': `${formValues.first_name}${formValues.middle_name ? ` ${formValues.middle_name} ` : ' '}${formValues.last_name}`,
						'contact_relationship': pointOfContactFormValues.dependent_type,
					};
				} else {
					payload = {
						...payload,
						'contact_phone': `${pointOfContactFormValues.contact_phone_country_code} ${pointOfContactFormValues.contact_phone_number_formated}`,
						'contact_email': pointOfContactFormValues.contact_email,
						'contact_name': pointOfContactFormValues.full_name_contact,
						'contact_relationship': pointOfContactFormValues.relationship_to_deceased,
					};
				}
			}
		} else {
			return;
		}
	} else if (!isWidowed) {
		if (maritalStatus !== MaritalStatuses.LegallySeparated && formValues?.marital_status === MaritalStatuses?.LegallySeparated) {
			payload = {
				legal_separation_date: formatDate(formValues.marriage_end_date),
				marriage_cert_recvd_indicator: '',
				start_date: spouse?.start_date,
			};
		} else if (formValues?.marital_status === MaritalStatuses.Divorced) {
			payload = {
				guid: spouse.person_guid,
				end_date: formatDate(formValues.marriage_end_date),
				case_number: formValues.case_number,
				court_issuing_decree: formValues.court_issuing,
			};
		} else {
			return;
		}
	}

	const marriageEndSuccess = () => {
		dispatch(setSpouseUnloaded(app));
		dispatch(setPersonWithHandler(personGuid, dispatchSetCurrentPerson(dispatch, app), () => dispatchSetCurrentPersonFailed(dispatch, app)(true)));
		dispatch(getSpouses(personGuid, false, dispatchSetSpouses(dispatch, app), () => dispatchSetSpousesFailed(dispatch, app)));
	};

	const divorcedSuccess = () => {
		marriageEndSuccess();
		dispatch(setDivorcedStatus(ApiStatuses.Success, app));
		if (onSubmitSucess) onSubmitSucess();
	};

	const legallySeparationSuccess = () => {
		marriageEndSuccess();
		dispatch(setLegalSeparationStatus(ApiStatuses.Success, app));
		if (onSubmitSucess) onSubmitSucess();
	};

	const widowedSuccess = () => {
		marriageEndSuccess();
		dispatch(setWidowedStatus(ApiStatuses.Success, app));
		if (onSubmitSucess) onSubmitSucess();
	};

	clearErrorsAndStatus(dispatch, app);

	const onDocUploadSuccessWidow = (data) => {
		const withDocuments = appendDocument(payload, data?.map(a => a?.data?.doc_id));
		dispatch(patchDeath(spouse.person_guid, withDocuments, widowedSuccess));
	};

	const onDocUploadSuccessDivorce = (data) => {
		const withDocuments = appendDocument(payload, data?.map(a => a?.data?.doc_id));
		dispatch(patchDivorce(personGuid, withDocuments, divorcedSuccess));
	};

	const onDocUploadSuccessLegallySeparation = (data) => {
		const withDocuments = appendDocument(payload, data?.map(a => a?.data?.doc_id));
		dispatch(patchLegallySeparation(personGuid, withDocuments, legallySeparationSuccess));
	};

	const uploadNotWidowDocument = () => {

		if (formValues?.marital_status === MaritalStatuses.LegallySeparated) {
			return onDocUploadSuccessLegallySeparation;
		}

		return onDocUploadSuccessDivorce;
	};

	if (docUploadState.stagedDocuments && docUploadState.stagedDocuments.length > 0) {
		processRequestWithDocumentUpload(docUploadState, personGuid, dispatch, isWidowed ? onDocUploadSuccessWidow : uploadNotWidowDocument());

	} else if (isWidowed) {
		dispatch(patchDeath(spouse.person_guid, payload, widowedSuccess));
	} else if (!isWidowed && formValues?.marital_status === MaritalStatuses.LegallySeparated) {
		dispatch(patchLegallySeparation(spouse?.person_guid, payload, legallySeparationSuccess));
	} else {
		dispatch(patchDivorce(personGuid, payload, divorcedSuccess));
	}

};